import { computed, unref } from 'vue';
import { useHead } from '@vueuse/head';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  TWITTER_HANDLE,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
} from '@/config/seo';

/**
 * Build a fully-qualified canonical URL for a given path on the canonical
 * domain. Strips any trailing slash (except root) for consistency.
 */
const buildCanonical = (path) => {
  const cleanPath = path === '/' ? '/' : path.replace(/\/$/, '');
  return `${SITE_URL}${cleanPath}`;
};

/**
 * Build hreflang alternates for every supported locale plus x-default.
 * URLs follow the existing `?lang=xx` strategy used across the app.
 */
const buildHreflangLinks = (path) => {
  const links = SUPPORTED_LOCALES.map((locale) => ({
    rel: 'alternate',
    hreflang: locale,
    href: `${SITE_URL}${path}${path.includes('?') ? '&' : '?'}lang=${locale}`,
  }));
  links.push({ rel: 'alternate', hreflang: 'x-default', href: buildCanonical(path) });
  return links;
};

/**
 * Composable that wires per-page SEO into the document head via @vueuse/head.
 *
 * Accepts an options object whose values may be plain strings, refs, or
 * computeds — everything is normalised through `unref` and recomputed when
 * dependencies (route, locale, options) change.
 *
 * @param {object} options
 * @param {string|Ref<string>} [options.title] - Final <title> (without suffix).
 * @param {string|Ref<string>} [options.titleKey] - i18n key for the title.
 * @param {string|Ref<string>} [options.description] - Meta description.
 * @param {string|Ref<string>} [options.descriptionKey] - i18n key for the description.
 * @param {string|Ref<string>} [options.ogImage] - Absolute URL for og:image.
 * @param {string|Ref<string>} [options.ogType] - og:type, defaults to "website".
 * @param {string|Ref<string>} [options.canonical] - Override canonical URL.
 * @param {boolean|Ref<boolean>} [options.noindex] - Emit noindex/nofollow robots tag.
 * @param {Array|Ref<Array>} [options.jsonLd] - Array of JSON-LD objects to inject.
 * @param {string|Ref<string>} [options.keywords] - Meta keywords (legacy).
 */
export const usePageSeo = (options = {}) => {
  const route = useRoute();
  const { t, locale } = useI18n();

  const head = computed(() => {
    const titleRaw = unref(options.title);
    const titleKey = unref(options.titleKey);
    const descriptionRaw = unref(options.description);
    const descriptionKey = unref(options.descriptionKey);
    const ogImage = unref(options.ogImage) || DEFAULT_OG_IMAGE;
    const ogType = unref(options.ogType) || 'website';
    const canonicalOverride = unref(options.canonical);
    const noindex = unref(options.noindex) === true;
    const keywords = unref(options.keywords);
    const jsonLd = unref(options.jsonLd) || [];

    const title = titleRaw || (titleKey ? t(titleKey) : SITE_NAME);
    const description = descriptionRaw || (descriptionKey ? t(descriptionKey) : '');

    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonical = canonicalOverride || buildCanonical(route.path);

    const meta = [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: ogType },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: ogImage },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:locale', content: unref(locale) || DEFAULT_LOCALE },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: TWITTER_HANDLE },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
    ];

    if (keywords) {
      meta.push({ name: 'keywords', content: keywords });
    }

    if (noindex) {
      meta.push({ name: 'robots', content: 'noindex, nofollow' });
    }

    const link = [
      { rel: 'canonical', href: canonical },
      ...buildHreflangLinks(route.path),
    ];

    const script = jsonLd
      .filter(Boolean)
      .map((data) => ({
        type: 'application/ld+json',
        children: typeof data === 'string' ? data : JSON.stringify(data),
      }));

    return {
      title: fullTitle,
      htmlAttrs: { lang: unref(locale) || DEFAULT_LOCALE },
      meta,
      link,
      script,
    };
  });

  useHead(head);

  return { head };
};

export default usePageSeo;
